import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaEye, FaEdit, FaReceipt, FaPaperPlane, FaDollarSign, FaTrashAlt } from 'react-icons/fa';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';

import { UserContext } from '../../UserContext';
import { useHttpClient } from '../hooks/useHttpClient';
import { useNotificacoes } from '../hooks/useNotificacoes';
import { usePreventDoubleClick } from '../hooks/usePreventDoubleClick';

import Pagination from '../pagination/Pagination';
import BotaoFatura from './BotaoFatura';
import BotaoBoletoAsaas from './BotaoBoletoAsaas';
import BotaoEnviarEmail from './BotaoEmail';
import BotaoBoletoDeletar from './BotaoBoletoDeletar';


import styles from './ListaConsumoClientes.module.css';
import styles_botao_email from './BotaoEmail.module.css';
import styles_global from '../css/global.module.css';
import styles_content from '../css/main-content.module.css';

export default function ListaConsumoClientes() {
    const { user } = useContext(UserContext);
    const navigate = useNavigate();
    const { get, post, del } = useHttpClient();
    const { exibirErro, exibirSucesso } = useNotificacoes();

    // 1) redireciona se não logado
    useEffect(() => {
        if (!user) navigate('/login');
    }, [user, navigate]);

    // estados
    const [usinas, setUsinas] = useState([]);
    const [consumoClientes, setConsumoClientes] = useState([]);
    const [filtros, setFiltros] = useState({
        idUsina: '',
        mes: new Date().getMonth() + 1,
        ano: new Date().getFullYear(),
        nome: '',
        cpfCnpj: ''
    });
    const [page, setPage] = useState(0);
    const [pageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(false);
    const initialized = useRef(false);

    // busca usinas apenas uma vez
    useEffect(() => {
        if (!user) return;
        (async () => {
            try {
                const resp = await get(
                    `/hello-sol/usinas/buscar?idEmpresa=${user.empresa.id}`
                );
                setUsinas(Array.isArray(resp) ? resp : resp.content ?? []);
            } catch (err) {
                exibirErro('Erro ao buscar usinas: ' + err.message);
            }
        })();
    }, [user]);

    // função de busca de consumos com paginação
    const fetchConsumoClientes = useCallback(
        async (newPage = 0) => {
            setLoading(true);
            try {
                const params = new URLSearchParams({
                    ...filtros,
                    page: newPage,
                    pageSize
                }).toString();

                const resp = await get(`/hello-sol/consumos-cliente?${params}`);
                setConsumoClientes(resp.content || []);
                setTotalPages(resp.totalPages ?? 0);
                setPage(newPage);
                initialized.current = true;
            } catch (err) {
                exibirErro('Erro ao buscar consumos: ' + err.message);
            } finally {
                setLoading(false);
            }
        },
        [get, filtros, pageSize, exibirErro]
    );

    const handleChange = e => {
        const { name, value } = e.target;
        setFiltros(f => ({
            ...f,
            [name]: value
        }));
    };


    // evita double-click no Buscar
    const [safeFetch] = usePreventDoubleClick(() => fetchConsumoClientes(0));

    // submit do formulário
    const handleSubmit = e => {
        e.preventDefault();
        safeFetch();
    };

    // exclusão com confirmação
    const confirmDelete = id => {
        confirmAlert({
            title: 'Confirmação',
            message: 'Deseja realmente excluir este consumo?',
            buttons: [
                {
                    label: 'Sim',
                    onClick: async () => {
                        try {
                            await del(`/hello-sol/consumos-cliente/${id}`);
                            exibirSucesso('Consumo excluído com sucesso.');
                            fetchConsumoClientes(page);
                        } catch (err) {
                            exibirErro('Erro ao excluir: ' + err.message);
                        }
                    }
                },
                { label: 'Não' }
            ]
        });
    };


    // paginação
    const handlePageChange = newPage => {
        if (newPage >= 0 && newPage < totalPages) {
            fetchConsumoClientes(newPage);
        }
    };

    const getMonthName = m =>
        new Date(0, m - 1).toLocaleString('pt-BR', { month: 'long' }).replace(/^\w/, c => c.toUpperCase());

    const formatDocumento = v =>
        v.length > 11
            ? v.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2}).*/, '$1.$2.$3/$4-$5')
            : v.replace(/^(\d{3})(\d{3})(\d{3})(\d{2}).*/, '$1.$2.$3-$4');

    return (
        <div className={styles_global.container}>
            <h2>Consultar Consumo</h2>

            <form onSubmit={handleSubmit} className={styles.filterFormConsumo}>
                <div className={`${styles_content.formField} ${styles.usinaField}`}>
                    <label>Usina</label>
                    <select
                        name="idUsina"
                        value={filtros.idUsina}
                        onChange={e =>
                            setFiltros(f => ({ ...f, idUsina: e.target.value }))
                        }
                        required
                    >
                        <option value="">Selecionar Usina</option>
                        {usinas.map(u => (
                            <option key={u.id} value={u.id}>
                                {u.nome}
                            </option>
                        ))}
                    </select>
                </div>

                <div className={`${styles_content.formField} ${styles.mesField}`}>
                    <label>Mês</label>
                    <select
                        name="mes"
                        value={filtros.mes}
                        onChange={e => setFiltros(f => ({ ...f, mes: e.target.value }))}
                    >
                        <option value="">Selecionar Mês</option>
                        {[...Array(12)].map((_, i) => (
                            <option key={i + 1} value={i + 1}>
                                {getMonthName(i + 1)}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Ano */}
                <div className={`${styles_content.formField} ${styles.anoField}`}>
                    <label>Ano</label>
                    <input
                        type="number"
                        name="ano"
                        value={filtros.ano}
                        onChange={handleChange}
                        min="2020"
                    />
                </div>

                <div className={`${styles_content.formField} ${styles.nomeField}`}>
                    <label>Nome</label>
                    <input
                        name="nome"
                        value={filtros.nome}
                        onChange={e => setFiltros(f => ({ ...f, nome: e.target.value }))}
                        placeholder="Nome"
                    />
                </div>

                <div className={`${styles_content.formField} ${styles.cpfField}`}>
                    <label>CPF/CNPJ</label>
                    <input
                        name="cpfCnpj"
                        value={filtros.cpfCnpj}
                        onChange={e =>
                            setFiltros(f => ({ ...f, cpfCnpj: e.target.value }))
                        }
                        placeholder="CPF ou CNPJ"
                    />
                </div>

                <div className={styles.botoesAbaixo}>
                    <button type="submit" disabled={loading}>
                        {loading ? 'Consultando...' : 'Consultar'}
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate('/home/cadastrar-cliente-consumo')}
                    >
                        Novo Consumo
                    </button>
                </div>
            </form>

            {loading && <p className={styles_global.loading}>Carregando...</p>}

            {!loading && consumoClientes.length > 0 && (
                <>
                    <div className={styles.tableContainer}>
                        <table className={styles.tableConsumo}>
                            <thead>
                            <tr>
                                <th>CPF/CNPJ</th>
                                <th>Nome</th>
                                <th>Cód. Cliente/Unidade</th>
                                <th>Período</th>
                                <th>Ações</th>
                            </tr>
                            </thead>
                            <tbody>
                            {consumoClientes.map(c => (
                                <tr key={c.id}>
                                    <td>{formatDocumento(c.unidadeConsumidora?.cliente?.cpfCnpj)}</td>
                                    <td>{c.unidadeConsumidora?.cliente?.nome}</td>
                                    <td>
                                        {c.unidadeConsumidora?.codCliente}
                                        {c.unidadeConsumidora?.nomeUnidade ? ` — ${c.unidadeConsumidora.nomeUnidade}` : ''}
                                    </td>
                                    <td>{getMonthName(c.mesConsumo)}</td>
                                    <td className={styles.actions}>
                                        <Link
                                            to={`/home/detalhar-cliente-consumo/${c.id}`}
                                            className={styles.iconButton}
                                        >
                                            <FaEye title="Visualizar" className={styles.icon}/>
                                        </Link>
                                        <Link
                                            to={`/home/cadastrar-cliente-consumo/${c.id}`}
                                            className={styles.iconButton}
                                        >
                                            <FaEdit title="Editar" className={styles.icon}/>
                                        </Link>
                                        <BotaoFatura idConsumo={c.id} className={styles.iconButton}>
                                            <FaReceipt title="Gerar Fatura"/>
                                        </BotaoFatura>
                                        {c.usina.chaveAsaas && (
                                            <BotaoBoletoAsaas idConsumo={c.id} className={styles.iconButton}>
                                                <FaDollarSign title="Gerar Boleto Asaas"/>
                                            </BotaoBoletoAsaas>
                                        )}
                                        <BotaoEnviarEmail
                                            idConsumo={c.id}
                                            className={`${styles.iconButton} ${styles_botao_email['botao-fatura']}`}
                                        >
                                            <FaPaperPlane title="Enviar fatura por E-mail"/>
                                        </BotaoEnviarEmail>

                                      <BotaoBoletoDeletar
                                          idConsumo={c.id}
                                          className={styles.iconButton}
                                      >
                                          <FaTrashAlt title="Deletar boleto" />
                                      </BotaoBoletoDeletar>



                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>

                    <Pagination
                        currentPage={page}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                    />
                </>
            )}

            {!loading && consumoClientes.length === 0 && initialized.current && (
                <p className={styles.noData}>
                    Nenhum consumo encontrado com os critérios de busca.
                </p>
            )}
        </div>
    );
}
